import dayjs from 'dayjs';
import { reconClient } from '../../../utils/axios';

export const formatReconTimestamp = (value) => {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format('DD-MM-YYYY HH:mm') : '—';
};

export const formatReconTimestampLocal = (value, locale = 'en') => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  });
};

export const formatReconDate = (value) => {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format('DD-MM-YYYY') : '—';
};

export const formatAedAmount = (value) => {
  if (value === null || value === undefined || value === '' || Number.isNaN(Number(value))) return '—';
  return new Intl.NumberFormat('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value));
};

/** Max integer-part digit count shown before truncating with an overflow indicator. */
export const MAX_AMOUNT_DISPLAY_DIGITS = 10;

/** Digits in the absolute integer part (ignores sign, commas, and decimals). */
export const getIntegerDigitCount = (value) => {
  const n = Number(value);
  if (value === null || value === undefined || value === '' || Number.isNaN(n)) return 0;
  if (!Number.isFinite(n)) return Number.POSITIVE_INFINITY;
  const abs = Math.abs(n);
  if (abs < 1) return 1;
  if (abs <= Number.MAX_SAFE_INTEGER) return String(Math.trunc(abs)).length;
  return Math.floor(Math.log10(abs)) + 1;
};

const leadingIntegerDigits = (abs, maxDigits) => {
  if (abs <= Number.MAX_SAFE_INTEGER) {
    return String(Math.trunc(abs)).slice(0, maxDigits);
  }
  const exp = Math.floor(Math.log10(abs));
  const mantissa = abs / 10 ** exp;
  return String(Math.floor(mantissa * 10 ** (maxDigits - 1)))
    .padStart(maxDigits, '0')
    .slice(0, maxDigits);
};

/**
 * Compact AED display: full format when integer digits ≤ 10; otherwise the first
 * 10 integer digits (comma-grouped) + ellipsis. `full` always holds the complete
 * formatted string for tooltips.
 */
export const formatCompactAedAmount = (value, { maxDigits = MAX_AMOUNT_DISPLAY_DIGITS } = {}) => {
  if (value === null || value === undefined || value === '' || Number.isNaN(Number(value))) {
    return { display: '—', full: '—', truncated: false };
  }
  const n = Number(value);
  const full = Number.isFinite(n) ? formatAedAmount(n) : String(value);
  const digitCount = getIntegerDigitCount(n);
  if (digitCount <= maxDigits) {
    return { display: full, full, truncated: false };
  }
  const abs = Math.abs(n);
  const leading = leadingIntegerDigits(Number.isFinite(abs) ? abs : Number.MAX_VALUE, maxDigits);
  const grouped = new Intl.NumberFormat('en-AE', { maximumFractionDigits: 0 }).format(Number(leading));
  const sign = n < 0 ? '-' : '';
  return { display: `${sign}${grouped}…`, full, truncated: true };
};

// Collection-name document_type → human label; unknown values shown raw.
const DOCUMENT_TYPE_LABELS = {
  sales_invoices: 'Sales invoices',
  sales_credit_notes: 'Sales credit notes',
  purchases_invoices: 'Purchase invoices',
  purchases_credit_notes: 'Purchase credit notes',
};

export const formatDocumentType = (value) => {
  if (!value) return '—';
  return DOCUMENT_TYPE_LABELS[value] ?? value;
};

// Authenticated file download through Kong: a plain link can't carry the
// Bearer token, so fetch as a blob and hand it to the browser.
export const downloadReconFile = async (path, params, fallbackName) => {
  const response = await reconClient.get(path, { params, responseType: 'blob' });
  const disposition = response.headers['content-disposition'] ?? '';
  const fileName = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? fallbackName;
  const url = URL.createObjectURL(response.data);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

// Excel export of the active classification with the current filters.
export const downloadExportExcel = (params, classification) =>
  downloadReconFile('/results/export', params, `reconciliation_${(classification ?? '').toLowerCase()}.xlsx`);

// Server-generated xlsx template for the ERP source file layout.
export const downloadTemplate = () => downloadReconFile('/uploads/template', undefined, 'erp_source_template.xlsx');
